import { useEffect } from 'react';
import { Box, IconButton } from '@nimbus-ds/components';
import { CogIcon, ExternalLinkIcon, IconProps } from '@nimbus-ds/icons';
import { Menu } from '@nimbus-ds/patterns';
import tokens from '@nimbus-ds/tokens/dist/js/tokens';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Country } from '@tiendanube/common/src/enums';
import Logo from 'App/components/Logo';
import ShowByRole from 'App/components/ShowByRole';
import { Avatar } from 'App/components/Topbar/components';
import {
  SOCIAL_OLD_SALES_CHANNELS_MENU,
  SOCIAL_TIKTOK_ADMIN,
  TRAFFIC_PINTEREST_ADMIN_TAG,
} from 'App/featuresFlags';
import { useHandleMenuClick, useHasTags, useNavegate } from 'App/hooks';
import { useDevMode } from 'App/settings/devMode/hooks';
import { useMenu } from 'App/settings/menu/hooks';
import {
  trackingMenuConfigurationClick,
  trackingMenuStoreVisit,
} from 'App/tracking';
import { useResponsive } from 'commons/components';
import { openWindow } from 'commons/utils/window';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import {
  useGetCountry,
  useGetIsEvolucion,
  useGetIsNext,
  useHeaderMenuData,
  useIsPlanLojinha,
} from 'domains/Auth/hooks';
import { useGetHasAccessToCustomers } from 'domains/Customers/hooks';
import { DashboardMenu } from 'domains/Dashboard';
import useOnboardingTasks from 'domains/Dashboard/pages/DashboardPage/components/OnboardingTasks/useOnboardingTasks';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useNuvemApps } from 'domains/PartnersApps/pages/NuvemAppPage/hooks';
import { useHasAccessToStats } from 'domains/Statistics';
import {
  AppsMenu,
  CustomersMenu,
  CustomizeMenu,
  ExternalAppsEnhanceMenu,
  GoogleShoppingMenu,
  KeyboardMenu,
  MainMenuDesktopHeader,
  MainMenuMobileHeader,
  MarketingAutomationMenu,
  MarketingMenu,
  MarketplacesMenu,
  MenuWithSpotlight,
  MetaMenu,
  NuvemEnvioMenu,
  NuvemPagoMenu,
  OrdersMenu,
  PosMenu,
  ProductsMenu,
  SalesChannelsMenu,
  StatsMenu,
  TikTokMenu,
  PinterestMenu,
} from './components';
import NuvemChatMenu from './components/NuvemChatMenu';
import { useGetDefaultSettingsOption } from './hooks';
import { MENU_PATHS } from './types';
import { useDesktopMenuContext } from '../../DesktopMenuProvider';
import { useMenuScrollShadow } from '../../hooks';
import './MainMenu.scss';

interface MainMenuProps {
  device: 'mobile' | 'desktop';
  onClickSupport: () => void;
}

const rolesForSettings = [
  'full',
  'shipping',
  'locations',
  'payments',
  'emails',
  'i18n',
  'preferences_checkout',
  'preferences_advanced',
  'seo_redirects',
  'domains',
  'products',
  'customers',
  'orders',
];

export const rolesForSettingsRevamp = [
  ...rolesForSettings,
  'payments_and_subscriptions',
  'invoice_history',
];

// eslint-disable-next-line max-statements
function MainMenu({ device, onClickSupport }: Readonly<MainMenuProps>) {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const { handleClickAccordion } = useHandleMenuClick();
  const { fetchNuvemApps } = useNuvemApps();
  const { validateRoles } = useRoles();
  const { goTo } = useNavegate();
  const isMobile = device === 'mobile';
  const { isMobile: isMobileResponsive } = useResponsive();
  const hasAccessToStats = useHasAccessToStats();
  const hasAccessToCustomers = useGetHasAccessToCustomers();
  const country = useGetCountry();
  const shouldShowSalesChannelsSection =
    validateRoles(['full', 'pos']) || validateRoles(['design', 'meta']);
  const shouldShowMaximizeSection =
    (validateRoles('marketing') && country === Country.BR) ||
    validateRoles(['full', 'apps']);
  const isPlanLojinha = useIsPlanLojinha();
  const { name, url } = useHeaderMenuData();

  const { partnerId } = useStoreInfo();
  const { fetchMenu, menu } = useMenu();
  const { fetchDevMode } = useDevMode();
  const menuBodyRef = useMenuScrollShadow();
  const { isPos } = useOnboardingTasks();

  const [hasSocialTiktokAdmin, hasOldSalesChannelsMenuTag, hasPinterestAdmin] =
    useHasTags([
      SOCIAL_TIKTOK_ADMIN,
      SOCIAL_OLD_SALES_CHANNELS_MENU,
      TRAFFIC_PINTEREST_ADMIN_TAG,
    ]);

  const { isExpandedMenu } = useDesktopMenuContext();

  // Always expand the menu on mobile. We use the responsive hook for Web Mobile.
  const expanded = isMobileResponsive ? true : isExpandedMenu;

  const shouldShowTikTokMenuItem =
    [Country.AR, Country.CL, Country.BR, Country.MX, Country.CO].includes(
      country,
    ) || hasSocialTiktokAdmin;

  const hasEnhanceApps =
    Object.keys(menu?.Main?.Enhance?.options || {}).length > 0;

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  useEffect(() => {
    fetchDevMode(partnerId);
  }, [fetchDevMode, partnerId]);

  const optionSettingsDefault = useGetDefaultSettingsOption();

  const handleClickSettings = () => {
    handleClickAccordion();
    goTo(MENU_PATHS[optionSettingsDefault]);
    trackingMenuConfigurationClick();
  };

  useEffect(() => {
    fetchNuvemApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function AvatarIcon({ ...props }: IconProps) {
    return <Avatar name={name} {...props} />;
  }

  const handleClickAccount = () => {
    handleClickAccordion();
    goTo(
      validateRoles('full')
        ? MENU_PATHS.businessData
        : MENU_PATHS.authenticationFactor,
    );
  };

  const acccountMenuMobile = {
    label: name,
    icon: AvatarIcon,
    onClick: handleClickAccount,
  };

  const settingsMenuDesktop = {
    label: t('menu.config'),
    icon: CogIcon,
    onClick: handleClickSettings,
  };

  const isValidateFullAndDesktop = validateRoles(rolesForSettingsRevamp)
    ? settingsMenuDesktop
    : undefined;

  const footer = isMobileResponsive
    ? acccountMenuMobile
    : isValidateFullAndDesktop;

  const isNext = useGetIsNext();
  const isEvolucion = useGetIsEvolucion();

  let headerLogo;
  let headerLogoColor;
  if (isNext) {
    headerLogo = 'Next';
    headerLogoColor = tokens.color.light.neutral['text-high'].value;
  } else if (isEvolucion) {
    headerLogo = 'Evolucion';
    headerLogoColor = tokens.color.light.neutral['text-high'].value;
  } else {
    headerLogo = language === 'pt-BR' ? 'Nuvemshop' : 'Tiendanube';
    headerLogoColor = tokens.color.light.primary.interactive.value;
  }

  useEffect(() => {
    if (isNext) {
      document.body.classList.add('stratus--plan-next');
    }
  }, [isNext]);

  const menuContainerClassName = classNames('stratus--menu-container', {
    'stratus--menu-container--next': isNext || isEvolucion,
  });

  const handleClickNews = () => {
    openWindow(t('topbar.whatsNewLink'), true);
  };

  const handleMenuStoreVisitClick = () => {
    trackingMenuStoreVisit();
    openWindow(url, true);
  };

  const menuContent = (
    <>
      {isMobileResponsive ? (
        <MainMenuMobileHeader
          onClickSupport={onClickSupport}
          onClickNews={handleClickNews}
          onClickProfile={handleClickAccount}
          name={name}
        />
      ) : (
        <MainMenuDesktopHeader
          headerLogo={headerLogo}
          headerLogoColor={headerLogoColor}
          expanded={expanded}
        />
      )}
      <div className="stratus--menu-body" ref={menuBodyRef}>
        <Menu.Body>
          {isMobileResponsive && (
            <Box>
              <Box
                paddingBottom="3"
                paddingTop="3"
                paddingLeft="2"
                paddingRight="2"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Logo src={headerLogo} color={headerLogoColor} />
                <IconButton
                  source={<ExternalLinkIcon />}
                  size="2rem"
                  aria-label={t('menu.visitStore')}
                  onClick={handleMenuStoreVisitClick}
                  tabIndex={-1}
                />
              </Box>
            </Box>
          )}

          <Menu.Section>
            <DashboardMenu />
            {hasAccessToStats && <StatsMenu />}
          </Menu.Section>
          {/* Administrar */}
          <ShowByRole
            includeRoles={[
              'orders',
              'draft_orders',
              'abandoned_carts',
              'products',
              'customers',
              'marketing',
              'nuvem_envio',
              'nuvem_pago',
              'nuvem_chat',
            ]}
          >
            <Menu.Section title={t('menu.manage.title')}>
              <OrdersMenu isMobile={isMobile} />
              <ProductsMenu isMobile={isMobile} />
              <NuvemPagoMenu />
              <NuvemEnvioMenu />
              <NuvemChatMenu />
              {hasAccessToCustomers && <CustomersMenu />}
              <MarketingMenu isMobile={isMobile} />
              <MarketingAutomationMenu />
            </Menu.Section>
          </ShowByRole>

          {/* Canales de venta */}
          {shouldShowSalesChannelsSection && (
            <Menu.Section title={`${t('menu.salesChannels.title')}`}>
              {isPos ? (
                <>
                  <ShowByRole includeRoles={['full', 'pos']}>
                    <PosMenu />
                  </ShowByRole>
                  <ShowByRole includeRoles="design">
                    <CustomizeMenu />
                  </ShowByRole>
                </>
              ) : (
                <>
                  <ShowByRole includeRoles="design">
                    <CustomizeMenu />
                  </ShowByRole>
                  <ShowByRole includeRoles={['full', 'pos']}>
                    <PosMenu />
                  </ShowByRole>
                </>
              )}

              {!isPlanLojinha &&
                !isMobile &&
                (hasOldSalesChannelsMenuTag ? (
                  <ShowByRole
                    includeRoles={['meta', 'google_shopping', 'tiktok']}
                  >
                    <SalesChannelsMenu />
                  </ShowByRole>
                ) : (
                  <>
                    <ShowByRole includeRoles={['meta']}>
                      <MetaMenu />
                    </ShowByRole>
                    <ShowByRole includeRoles={['google_shopping']}>
                      <GoogleShoppingMenu />
                    </ShowByRole>
                    {shouldShowTikTokMenuItem && (
                      <ShowByRole includeRoles={['tiktok']}>
                        <TikTokMenu />
                      </ShowByRole>
                    )}
                    {hasPinterestAdmin && <PinterestMenu />}
                  </>
                ))}
              {!isPlanLojinha && (
                <ShowByRole includeRoles={['full']}>
                  <MarketplacesMenu />
                </ShowByRole>
              )}
            </Menu.Section>
          )}

          {/* Potenciar */}
          {shouldShowMaximizeSection && (
            <Menu.Section title={`${t('menu.maximize.title')}`}>
              <ShowByRole includeRoles={['full', 'apps']}>
                <AppsMenu isMobile={isMobile} />
              </ShowByRole>
              {isMobile && <KeyboardMenu />}
            </Menu.Section>
          )}
          {!isMobile && hasEnhanceApps && (
            <Menu.Section
              title={
                shouldShowMaximizeSection
                  ? undefined
                  : `${t('menu.maximize.title')}`
              }
            >
              <ExternalAppsEnhanceMenu isMobile={isMobile} />
            </Menu.Section>
          )}
        </Menu.Body>
      </div>
      {footer && (
        <>
          {/* Configuración Mobile */}
          {isMobileResponsive ? (
            <ShowByRole includeRoles={rolesForSettings}>
              <div className="stratus--configurations-footer-mobile">
                <Menu.Footer
                  label={t('menu.config')}
                  startIcon={CogIcon}
                  onClick={handleClickSettings}
                />
              </div>
            </ShowByRole>
          ) : (
            <Menu.Footer
              label={footer?.label || ''}
              startIcon={footer?.icon}
              onClick={footer?.onClick}
            />
          )}
        </>
      )}
    </>
  );

  return (
    <div className={menuContainerClassName}>
      {isNext || isEvolucion ? (
        <MenuWithSpotlight expanded={expanded}>{menuContent}</MenuWithSpotlight>
      ) : (
        <Menu expanded={expanded}>{menuContent}</Menu>
      )}
    </div>
  );
}

export default MainMenu;
